import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { getPopupRegistration, openPopup, Priority } from 'app/PopupHolder';
import Confirm from 'app/components/Confirm';
import Button from 'components/base/Button';
import Modal from 'components/base/Modal';
import ListManagementField from '../../../components/ListManagementField';
import Select from '../../../components/Select';

import css from './style.scss';

class AddToFolder extends PureComponent {
  constructor(props) {
    super(props);

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleListSelect = this.handleListSelect.bind(this);
    this.handleParentSelect = this.handleParentSelect.bind(this);

    const { folders: allFolders, type, folderKey, editMode } = props;

    // Ensure folders is an array
    const foldersArray = Array.isArray(allFolders) ? allFolders : [];

    const folderMap = foldersArray.reduce((m, f) => ({ ...m, [f.key]: f }), {});

    // Transform folders to match ListManagementField expected structure
    this.folders = foldersArray
      .filter(f => f.type === type)
      .map(f => ({ 
        id: f.key,
        value: f.key, 
        label: `${f.parentKey && folderMap[f.parentKey] ? `${folderMap[f.parentKey].name} / ` : ''}${f.name}`, 
        name: f.name, 
        parentKey: f.parentKey 
      }));
    
    // Sort by full name
    this.folders.sort((a, b) => a.label.localeCompare(b.label));

    // Find current folder
    this.folder = this.folders.find(f => f.value === folderKey);

    // Root folders for parent dropdown - include ALL folders that can be parents
    // For editMode, exclude the current folder and its children to prevent circular references
    this.rootFolders = this.folders
      .filter(f => {
        // Only show folders without a parent (root folders)
        if (f.parentKey) return false;
        
        // Exclude the current folder itself
        if (f.value === folderKey) return false;
        
        // For editMode, also exclude children of the current folder to prevent circular references
        if (editMode && folderKey) {
          // Check if this folder is a child of the current folder
          let current = f;
          while (current.parentKey) {
            if (current.parentKey === folderKey) return false;
            const parent = this.folders.find(p => p.value === current.parentKey);
            if (!parent) break;
            current = parent;
          }
        }
        
        return true;
      })
      .map(f => ({ 
        id: f.value,
        name: f.name,
        value: f.value,
        label: f.name
      }));
    
    // Add "No Parent" option for Select
    this.rootFolders.unshift({ 
      id: 'no-parent', 
      name: '<No Parent>',
      value: '',
      label: '<No Parent>'
    });

    // Initialize state
    if (this.folder) {
      this.state = {
        selection: this.folder.value,
        parentSelection: this.folder.parentKey || '',
        name: this.folder.name,
        mode: 'add',
        selectedFolder: this.folder,
        selectedList: this.folder
      };
    } else {
      // Set default selection to first folder or null
      const defaultFolder = this.folders[0] || null;
      this.state = {
        selection: defaultFolder ? defaultFolder.value : '',
        parentSelection: '',
        name: '',
        mode: 'new',
        selectedFolder: null,
        selectedList: defaultFolder
      };
    }
  }

  handleListSelect(list) {
    // Handle selection from ListManagementField
    if (list && typeof list === 'object') {
      // Check if it's an existing folder (has id)
      if (list.id) {
        const existingFolder = this.folders.find(f => f.value === list.id);
        if (existingFolder) {
          this.setState({
            selectedFolder: existingFolder,
            selectedList: existingFolder,
            selection: existingFolder.value,
            mode: 'add',
            name: existingFolder.name,
            parentSelection: existingFolder.parentKey || '',
          });
        }
      } 
      // Check if it's a new folder creation (has name but no id)
      else if (list.name && !list.id) {
        const folderName = list.name;
        // Check if folder with this name already exists
        const existing = this.folders.find(f => f.name.toLowerCase() === folderName.toLowerCase());
        if (existing) {
          // If exists, select it
          this.setState({
            selectedFolder: existing,
            selectedList: existing,
            selection: existing.value,
            mode: 'add',
            name: existing.name,
            parentSelection: existing.parentKey || '',
          });
        } else {
          // Create new
          this.setState({
            mode: 'new',
            name: folderName,
            selectedFolder: { value: '', name: folderName },
            selectedList: { name: folderName },
            selection: '',
            parentSelection: this.props.editMode ? this.state.parentSelection : '',
          });
        }
      }
    } else if (typeof list === 'string') {
      // If string, treat as new folder name
      const folderName = list.trim();
      if (folderName) {
        // Check if folder with this name already exists
        const existing = this.folders.find(f => f.name.toLowerCase() === folderName.toLowerCase());
        if (existing) {
          this.setState({
            selectedFolder: existing,
            selectedList: existing,
            selection: existing.value,
            mode: 'add',
            name: existing.name,
            parentSelection: existing.parentKey || '',
          });
        } else {
          this.setState({
            mode: 'new',
            name: folderName,
            selectedFolder: { value: '', name: folderName },
            selectedList: { name: folderName },
            selection: '',
            parentSelection: this.props.editMode ? this.state.parentSelection : '',
          });
        }
      }
    }
  }

  handleParentSelect(item) {
    // Handle parent folder selection from Select component
    if (item) {
      // Check if "No Parent" is selected
      if (item.id === 'no-parent' || item.value === '') {
        this.setState({ parentSelection: '' });
      } else {
        // Find the selected parent folder
        const parentFolder = this.folders.find(f => f.value === item.id);
        this.setState({ 
          parentSelection: parentFolder ? parentFolder.value : '' 
        });
      }
    } else {
      this.setState({ parentSelection: '' });
    }
  }

  handleSubmit(ev) {
    ev.preventDefault();

    const { alert, closePopup, onSubmit, type, folderKey, editMode } = this.props;
    const { mode, selection, parentSelection, name } = this.state;
    const nameTrimmed = name.trim();
    
    closePopup();
    
    // Determine the key based on editMode
    let key;
    if (editMode) {
      // When in edit mode, always use the folderKey (the folder being edited)
      key = folderKey;
    } else if (mode === 'new') {
      // When creating a new folder (not in edit mode), key should be empty
      key = '';
    } else {
      // When selecting an existing folder, use the selection
      key = selection;
    }

    onSubmit({
      type,
      key,
      parentKey: parentSelection,
      name: nameTrimmed,
    });
  }

  handleChange(ev) {
    const { name, value } = ev.target || ev;
    this.setState({ [name]: value });
  }

  render() {
    const { loading, closePopup, editMode } = this.props;
    const { mode, parentSelection, selectedList, name } = this.state;

    const btn = { isLoading: loading, size: Button.size.large, kind: Button.kind.default };

    // Find the selected parent for the Select component
    const selectedParentId = parentSelection || 'no-parent';

    const isNameEmpty = !name || name.trim() === '';
    
    // Show parent folder dropdown in these cases:
    // 1. There are root folders available (more than just "No Parent")
    // 2. AND we're in editMode OR mode is 'add' (selecting existing folder)
    const shouldShowParentDropdown = this.rootFolders && 
      this.rootFolders.length > 1 && 
      (editMode || mode === 'add');

    // Determine the caption based on mode
    let caption;
    if (editMode) {
      caption = this.folder ? 'Edit Folder' : 'Add New Folder';
    } else {
      caption = this.folder ? 'Move List to Folder' : 'Add List to Folder';
    }

    return (
      <Modal
        isOpen
        uniqId="addToFolder"
        caption={caption}
        width="504px"
        padding="45px"
      >
        <form className={css.folders} onSubmit={this.handleSubmit} key="bottom">
          <div className={css.groupType}>
            {editMode ? (
              // For Edit Mode: Show simple input field
              <div>
                <div className={css.label}>Folder Name</div>
                <input
                  type="text"
                  name="name"
                  value={name || ''}
                  onChange={this.handleChange}
                  placeholder="Enter folder name"
                  className={css.input}
                  autoFocus
                />
              </div>
            ) : (
              // For Add/Move Mode: Show ListManagementField with type-ahead
              <ListManagementField
                lists={this.folders || []}
                onSelect={this.handleListSelect}
                onCreate={(newName) => this.handleListSelect({ name: newName })}
                placeholder="Select or Type to Create a New Folder"
                initialList={selectedList?.name || ''}
                label={"Folder Name"}
              />
            )}
          </div>
          
          {shouldShowParentDropdown && (
            <div className={css.groupType} style={{ marginTop: '34px' }}>
              <div className={css.label}>Parent Folder</div>
              <Select
                placeholder="Select Parent Folder"
                lists={this.rootFolders || []}
                selectedValue={selectedParentId}
                onSelect={this.handleParentSelect}
              />
            </div>
          )}
          
          <div className={css.buttons} style={{ marginTop: '34px' }}>
            <button {...btn} className={css.btnSecondary} onClick={closePopup}>Cancel</button>
            <button {...btn} className={css.btnPrimary} disabled={isNameEmpty} type="submit">Save</button>
          </div>
        </form>
      </Modal>
    );
  }
}

AddToFolder.propTypes = {
  folders: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
  onSubmit: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  folderKey: PropTypes.string,
  newEnabled: PropTypes.bool,
  editMode: PropTypes.bool,
};

AddToFolder.defaultProps = {
  newEnabled: true,
  editMode: false,
  folderKey: '',
};

const AddToFolderPopup = connect(() => ({}), {
  confirm: Confirm.alert,
})(AddToFolder);

AddToFolderPopup.open = props => openPopup(getPopupRegistration(AddToFolderPopup), { ...props, priority: Priority.MEDIUM_HIGH });

export default AddToFolderPopup;